import type { StatusKey } from "@/components/status-badge";
import { cn } from "@/lib/utils";

/** Neutral fallback used when a user-configured status has no color. */
export const STATUS_DOT_FALLBACK_COLOR = "#94A3B8";

export type StatusDotSize = "sm" | "md";

const STATUS_DOT_COLOR_CLASSES: Record<StatusKey, string> = {
  pending: "bg-status-pending",
  processing: "bg-status-processing",
  review: "bg-status-review",
  completed: "bg-status-completed",
  error: "bg-status-error",
};

const STATUS_DOT_SIZE_CLASSES: Record<StatusDotSize, string> = {
  sm: "size-2",
  md: "size-2.5",
};

/** Theme-token background for a canonical status dot (`muted` → neutral). */
export function statusDotColorClass(status: StatusKey, tone: "default" | "muted" = "default") {
  return tone === "muted" ? "bg-muted-foreground/50" : STATUS_DOT_COLOR_CLASSES[status];
}

export interface StatusDotProps {
  /** Canonical status → theme `--status-*` token. Ignored when `colorHex` is set. */
  status?: StatusKey;
  /** User-configured color (FieldPilot/TakeoffPilot custom statuses). */
  colorHex?: string | null;
  tone?: "default" | "muted";
  size?: StatusDotSize;
  /**
   * Accessible name. With a label the dot is `role="img"`; without one it is decorative
   * (`aria-hidden`) and must sit next to visible status text.
   */
  label?: string;
  className?: string;
}

/** A small filled circle that colors a status. Never the only carrier of meaning. */
export function StatusDot({
  status,
  colorHex,
  tone = "default",
  size = "md",
  label,
  className,
}: StatusDotProps) {
  const usesHex = colorHex !== undefined || status === undefined;
  return (
    <span
      data-slot="status-dot"
      {...(label ? { role: "img", "aria-label": label } : { "aria-hidden": true })}
      className={cn(
        "inline-block shrink-0 rounded-full",
        STATUS_DOT_SIZE_CLASSES[size],
        !usesHex && status ? statusDotColorClass(status, tone) : null,
        className
      )}
      style={usesHex ? { backgroundColor: colorHex ?? STATUS_DOT_FALLBACK_COLOR } : undefined}
    />
  );
}
